import { createSlice } from '@reduxjs/toolkit';

import type {
  AssignedVehicleRead,
  AssignmentRead,
} from 'src/common/external/bambi-api/assignmentsApi';
import type { VehicleRead } from 'src/common/external/bambi-api/bambiApi';

import { convertVehicleToAssignedVehicle } from './convertVehicleToAssignedVehicle';

export enum AssignmentManagementDisplayMode {
  PLAN_BY = 'plan_by',
  DATAGRID = 'datagrid',
}

export interface IAssignmentManagementState {
  assignments: AssignmentRead[];
  selectedDay?: string;
  assignmentSearchTerm: string;
  selectedAssignment: AssignmentRead | undefined;
  assignmentToClone?: AssignmentRead | null;
  showAssignmentManagementModal: boolean;
  showAssignmentExportModal: boolean;
  selectedVehicle: AssignedVehicleRead | undefined;
  showMultiAssignmentManagementModal: boolean;
  error: string;
  isSubmitting: boolean;
  displayMode: AssignmentManagementDisplayMode;
  dataGridSelectedRange?: { from: string; to: string };
  filterDrivers: string[];
  filterVehicles: string[];
  selectedAssignmentRows: Record<string, boolean>;
  showAssignmentTransferModal: boolean;
}

export const initialState: IAssignmentManagementState = {
  assignments: [],
  selectedDay: undefined,
  assignmentSearchTerm: '',
  selectedAssignment: undefined,
  showAssignmentManagementModal: false,
  showAssignmentExportModal: false,
  selectedVehicle: undefined,
  showMultiAssignmentManagementModal: false,
  error: '',
  isSubmitting: false,
  displayMode: AssignmentManagementDisplayMode.PLAN_BY,
  dataGridSelectedRange: undefined,
  filterDrivers: [],
  filterVehicles: [],
  selectedAssignmentRows: {},
  showAssignmentTransferModal: false,
};

export const assignmentManagementSlice = createSlice({
  name: 'assignmentManagement',
  initialState,
  reducers: {
    setAssignments: (state, action) => {
      state.assignments = action.payload;
    },
    setSelectedDay: (state, action) => {
      state.selectedDay = action.payload;
    },
    setAssignmentSearchTerm: (state, action) => {
      state.assignmentSearchTerm = action.payload;
    },
    setSelectedAssignment: (state, action) => {
      state.selectedAssignment = action.payload;
    },
    setShowAssignmentManagementModal: (state, action) => {
      state.showAssignmentManagementModal = action.payload;
    },
    setShowAssignmentExportModal: (state, action) => {
      state.showAssignmentExportModal = action.payload;
    },
    setShowMultiAssignmentManagementModal: (state, action) => {
      state.showMultiAssignmentManagementModal = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSelectedVehicle: (
      state,
      action: { payload: VehicleRead | undefined; type: string }
    ) => {
      if (action.payload === undefined) {
        state.selectedVehicle = undefined;
        return;
      }

      state.selectedVehicle = convertVehicleToAssignedVehicle(action.payload);
    },
    setAssignmentManagementDisplayMode: (state, action) => {
      state.displayMode = action.payload;
    },
    setDataGridSelectedRange: (state, action) => {
      state.dataGridSelectedRange = action.payload;
    },
    setFilterVehicles: (state, action) => {
      state.filterVehicles = action.payload;
    },
    setFilterDrivers: (state, action) => {
      state.filterDrivers = action.payload;
    },
    setSelectedAssignmentRows: (state, action) => {
      state.selectedAssignmentRows = action.payload;
    },
    onError: (state, action) => {
      state.error = action.payload;
      state.isSubmitting = false;
    },
    submit: (state) => {
      state.error = '';
      state.isSubmitting = true;
    },
    onSuccess: (state) => {
      state.error = '';
      state.isSubmitting = false;
      state.selectedAssignment = undefined;
      state.selectedVehicle = undefined;
      state.showAssignmentManagementModal = false;
      state.showAssignmentTransferModal = false;
    },
    onStartAssignmentClone: (state, action: { payload: AssignmentRead }) => {
      // We use a common component for both adding and editing
      // Unselecting takes us out of editing
      state.selectedAssignment = undefined;
      // This is probably unnecessary, but ensure the modal is open
      state.showAssignmentManagementModal = true;

      // This sets the vehicle for the modal
      state.selectedVehicle = action.payload.vehicle;
      state.assignmentToClone = action.payload;
    },
    onAssignmentCloneConfirm: (state) => {
      state.assignmentToClone = null;
    },
    setShowAssignmentTransferModal: (state, action) => {
      state.showAssignmentTransferModal = action.payload;
    },
    onCloseAssignmentModal: (state) => {
      state.selectedAssignment = undefined;
      state.selectedVehicle = undefined;
      state.showAssignmentManagementModal = false;
      state.assignmentToClone = null;
      state.error = '';
      state.showAssignmentTransferModal = false;
    },
  },
});

export const {
  setSelectedDay,
  setAssignments,
  setAssignmentSearchTerm,
  setSelectedAssignment,
  setShowAssignmentManagementModal,
  setShowAssignmentExportModal,
  setSelectedVehicle,
  setShowMultiAssignmentManagementModal,
  setError,
  setAssignmentManagementDisplayMode,
  setDataGridSelectedRange,
  setFilterVehicles,
  setFilterDrivers,
  onStartAssignmentClone,
  onCloseAssignmentModal,
  onAssignmentCloneConfirm,
  setSelectedAssignmentRows,
  setShowAssignmentTransferModal,
} = assignmentManagementSlice.actions;
